import { Icon } from '../../components/Icon';
import { ProgressRing } from '../../components/ProgressRing';
import { AcademicProfile, CalendarData, HomeData, UserDataStatus, WhaleuFeed } from '../../lib/api';

interface HomeContextOverviewProps {
  home?: HomeData;
  calendar?: CalendarData;
  userData?: UserDataStatus;
  academic?: AcademicProfile;
  whaleu: WhaleuFeed;
  onOpenNotice: () => void;
}

const DAY = 24 * 60 * 60 * 1000;

function dateKey(value: Date) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit' }).format(value);
}

function nextSevenDays(today: string, calendar?: CalendarData) {
  const start = new Date(`${today}T12:00:00+08:00`);
  return Array.from({ length: 7 }, (_, index) => {
    const date = dateKey(new Date(start.getTime() + index * DAY));
    const events = calendar?.events.filter((item) => item.date === date && !item.done).length || 0;
    const todos = calendar?.todos.filter((item) => item.due === date && !item.done).length || 0;
    return {
      date,
      label: index === 0 ? '今' : new Intl.DateTimeFormat('zh-CN', { weekday: 'short', timeZone: 'Asia/Shanghai' }).format(new Date(`${date}T12:00:00+08:00`)).replace('周', ''),
      count: events + todos,
    };
  });
}

export function HomeContextOverview({ home, calendar, userData, academic, whaleu, onOpenNotice }: HomeContextOverviewProps) {
  const today = home?.today || dateKey(new Date());
  const todoStats = home?.todoStats;
  const completion = todoStats?.todayProgress ?? 0;
  const completionLabel = todoStats?.todayProgress === null ? '今日无到期' : '今日待办';
  const completionDetail = todoStats?.todayProgress === null
    ? `${todoStats.open} 项已规划`
    : `${todoStats?.doneToday || 0}/${(todoStats?.doneToday || 0) + (todoStats?.dueToday || 0)}`;
  const overdue = todoStats?.overdue ?? home?.overview.overdueTodos ?? 0;
  const authorizations = userData?.authorizations;
  const connected = authorizations ? [authorizations.courses, authorizations.roster, authorizations.grades].filter(Boolean).length : 0;
  const days = nextSevenDays(today, calendar);
  const maxLoad = Math.max(1, ...days.map((day) => day.count));
  const personalizedCount = whaleu.items.filter((item) => (item.score || 0) > 0).length;

  return <>
    <section className="root-context-card ornamental-card" aria-labelledby="root-context-title">
      <div className="root-context-copy">
        <div className="section-kicker"><span>今日根脉</span><i>CONTEXT MAP</i></div>
        <h2 id="root-context-title">一眼看清，今天从哪里开始。</h2>
        <p>日程、待办、校园信息和学习数据在这里汇合；数字只来自你已经连接的应用。</p>
        <div className="context-primary-metric">
          <ProgressRing value={completion} label={completionLabel} detail={completionDetail} tone="cinnabar" />
          <dl>
            <div><dt>仍需处理</dt><dd>{home?.overview.openTodos ?? '—'}<small>项</small></dd></div>
            <div><dt>已经逾期</dt><dd className={overdue ? 'attention' : ''}>{overdue}<small>项</small></dd></div>
            <div><dt>本周到期</dt><dd>{todoStats?.dueThisWeek ?? '—'}<small>项</small></dd></div>
          </dl>
        </div>
      </div>
      <div className="root-load-panel">
        <header><div><span>未来七日负荷</span><small>日程 + 到期待办</small></div><strong>{days.reduce((sum, day) => sum + day.count, 0)}<small>项</small></strong></header>
        <div className="load-bars" role="img" aria-label="未来七日事项负荷">
          {days.map((day) => <div key={day.date} className={day.label === '今' ? 'today' : ''}><span><b>{day.count || ''}</b><i style={{ height: `${Math.max(day.count ? 18 : 4, day.count / maxLoad * 100)}%` }} /></span><small>{day.label}</small></div>)}
        </div>
        <div className="root-thread" aria-hidden="true"><i /><i /><i /><i /></div>
      </div>
    </section>

    <section className="context-card-grid" aria-label="应用状态">
      <a className="context-mini context-mini--calendar" href="/calendar">
        <span className="context-mini-icon"><Icon name="calendar" /></span><p><small>CALENDAR</small><b>本周安排</b></p><strong>{home?.overview.importantEvents ?? 0}<small> 个重要日程</small></strong><i>›</i>
      </a>
      <a className="context-mini context-mini--task" href="/calendar#todos">
        <span className="context-mini-icon"><Icon name="spark" /></span><p><small>TASK</small><b>{todoStats?.pressure === 'overdue' ? '逾期待处理' : '今日待办'}</b></p><strong>{todoStats?.todayProgress === null ? (todoStats.open || 0) : Math.round(completion)}<small>{todoStats?.todayProgress === null ? ' 项已规划' : '% 今日完成'}</small></strong><i>›</i>
      </a>
      <button className="context-mini context-mini--notice" type="button" onClick={onOpenNotice}>
        <span className="context-mini-icon"><Icon name="notice" /></span><p><small>INBOX</small><b>校园通知</b></p><strong>{home?.overview.unreadNotifications ?? 0}<small> 条未读</small></strong><i>›</i>
      </button>
      <a className="context-mini context-mini--signal" href="/whaleu">
        <span className="context-mini-icon">信</span><p><small>WHALEU</small><b>与你有关</b></p><strong>{personalizedCount || whaleu.items.length}<small> 条新信号</small></strong><i>›</i>
      </a>
      <a className="context-mini context-mini--learning" id="data" href="/account#academic-profile">
        <span className="context-mini-icon"><Icon name="book" /></span><p><small>GROWTH</small><b>成长画像</b></p><strong>{academic?.credits.progress !== null && academic?.credits.progress !== undefined ? `${academic.credits.progress}%` : academic?.currentCourses.length || connected}<small>{academic?.credits.progress !== null && academic?.credits.progress !== undefined ? ' 学分进度' : academic?.currentCourses.length ? ' 个排课记录' : '/ 3 已连接'}</small></strong><i>›</i>
      </a>
    </section>
  </>;
}
